import PersonIcon from "@mui/icons-material/Person";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { neutral } from "app/themes/colors";

import { LILAC, PLUM_600, PLUM_900 } from "./dialstack-softphone-widget.styles";
import type { Colleague } from "./use-colleague-lookup.hook";

type ColleagueCallCardProps = {
  // "Internal call" when they rang us, "Calling" when we rang them.
  caption: string;
  colleague: Colleague;
};

// A call with a colleague. Deliberately has no Quick add / Attach / Save as
// contact: there is no patient on the line to file it under.
export default function ColleagueCallCard({
  caption,
  colleague
}: ColleagueCallCardProps) {
  const { name, extension } = colleague;
  const title = name ?? (extension ? `Ext ${extension}` : "Colleague");
  // A leg from a colleague's web softphone has no extension to show; until
  // the directory names them, "Web phone" says why (ENG-2061).
  let subtitle = "Colleague";
  if (name && extension) subtitle = `Ext ${extension} · Colleague`;
  else if (!name && !extension) subtitle = "Web phone";
  return (
    <Box
      data-testid="colleague-call-card"
      sx={{
        mx: 1.25,
        mt: 1.25,
        px: 1.5,
        py: 1,
        borderRadius: "14px",
        bgcolor: "rgba(255, 255, 255, 0.96)",
        boxShadow: "0 6px 14px -8px hsla(271, 30%, 15%, 0.5)"
      }}
    >
      <Typography sx={{ fontSize: 12, color: neutral[600] }}>
        {caption}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: LILAC }}>
          <PersonIcon sx={{ fontSize: 18, color: PLUM_600 }} />
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            noWrap
            sx={{ fontSize: 15, fontWeight: 700, color: PLUM_900 }}
            data-testid="colleague-call-name"
          >
            {title}
          </Typography>
          <Typography
            noWrap
            sx={{ fontSize: 12, color: neutral[700] }}
            data-testid="colleague-call-subtitle"
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
